/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import React, {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useState,
} from 'react'
import { App, Modal, Alert, Tag, Spin } from 'antd'
import { CloseOutlined } from '@ant-design/icons'
import useSocialFeed, { Post } from '@sportninja/common/hooks/useSocialFeed'
import colors from '@sportninja/common/constants/appColors'
import Text from '../ts-components/Text'
import Button from '../ts-components/Button'
import { getSelfUser } from '@sportninja/common/selectors/users'
import { connect } from 'react-redux'
import { getErrorMessage } from '@sportninja/common/utils/utils'
import {
  AudienceBreakdownSection,
  NotificationWarningModal,
  PostHeader,
  CommunicationChannel,
  PostForm,
  PostSettings,
  type AudienceBreakdownData,
} from './components'
import { useEntityData } from './hooks/useEntityData'
import { useAudienceCalculator } from './hooks/useAudienceCalculator'
import { initialState, formReducer } from './reducer/useFeedPostReducer'
import { ROLE_TYPES } from './components/PostSettings'
import { sanitizeContentForSave } from 'src/utils/sanitizeContentForSave'

const styles = css`
  .my-modal-body {
    /* Custom scrollbar styling for webkit browsers */
    &::-webkit-scrollbar {
      width: 8px;
    }

    &::-webkit-scrollbar-track {
      background: ${colors.SECONDARY_400};
      border-radius: 4px;
    }

    &::-webkit-scrollbar-thumb {
      background: ${colors.SECONDARY_100};
      border-radius: 4px;

      &:hover {
        background: ${colors.SECONDARY_75};
      }
    }

    /* Firefox scrollbar styling */
    scrollbar-width: thin;
    scrollbar-color: ${colors.SECONDARY_100} ${colors.SECONDARY_400};
  }
  .my-modal-mask {
    backdrop-filter: blur(10px);
  }
  .my-modal-header {
  }
  .my-modal-footer {
    color: ${colors.FIERY_RED};
  }
  .my-modal-content {
    border: 1px solid #333;
  }
`
const classNames = {
  body: 'my-modal-body',
  mask: 'my-modal-mask',
  header: 'my-modal-header',
  footer: 'my-modal-footer',
  content: 'my-modal-content',
}
const modalStyles = {
  header: {
    backgroundColor: colors.SECONDARY_300,
    paddingLeft: '16px',
    paddingRight: '16px',
  },
  body: {
    borderBottom: `1px solid ${colors.SOFT_STEEL}`,
    borderTop: `1px solid ${colors.SOFT_STEEL}`,
    backgroundColor: colors.SECONDARY_300,
    padding: '0 16px 0 16px',
    maxHeight: 'calc(90vh - 200px)',
    overflowY: 'auto' as const,
    overflowX: 'hidden' as const,
  },
  content: {
    backgroundColor: colors.SECONDARY_300,
    paddingTop: '8px',
    paddingLeft: '0',
    paddingRight: '0',
  },
  wrapper: {
    zIndex: 10001,
  },
  mask: {
    backdropFilter: 'blur(10px)',
    backgroundColor: 'rgba(6, 10, 16, 0.8)',
    zIndex: 10000,
    overflow: 'hidden' as const,
  },
  footer: {
    paddingLeft: '16px',
    paddingRight: '16px',
  },
}

/**
 * @interface FeedPostCreateModalProps
 * @property {boolean} isOpen - Controls the visibility of the modal
 * @property {() => void} onClose - Callback executed when the modal is requested to close
 * @property {string} entityType - The type of the entity the feed belongs to (e.g., 'organization', 'team', 'schedule')
 * @property {string} entityId - The ID of the entity where the post will be created
 * @property {(page: number) => void} refreshNewFeed - Callback to refresh the parent feed view after post creation/update
 * @property {Post | null} postToEdit - The post data for editing. If null, the modal is in "create" mode
 * @property {(post: Post | null) => void} setPostToEdit - Callback to clear the post being edited
 * @property {any} selfUser - Current user data (should be properly typed in the future)
 */
interface FeedPostCreateModalProps {
  isOpen: boolean
  onClose: () => void
  entityType: string
  entityId: string
  refreshNewFeed: (page: number) => void
  postToEdit: Post | null
  setPostToEdit: (post: Post | null) => void
  selfUser: any
  entity?: any
  canTargetAudience: boolean
}

/**
 * A modal component for creating and editing social feed posts.
 *
 * This component provides a comprehensive form interface for users to:
 * - Create new posts with title, content, images, and links
 * - Edit existing posts
 * - Configure post visibility and permissions
 * - Send posts via email and push notifications
 * - Target specific audiences (organizations, teams, schedules)
 *
 * The component uses useReducer for efficient state management and React.memo
 * for performance optimization to prevent unnecessary re-renders.
 *
 * @param props - Component props as defined in FeedPostCreateModalProps
 * @returns JSX element representing the post creation/editing modal
 */
const FeedPostCreateModal: React.FC<FeedPostCreateModalProps> = React.memo(
  ({
    isOpen,
    onClose,
    entityType,
    entityId,
    refreshNewFeed,
    postToEdit,
    setPostToEdit,
    selfUser,
    entity,
    canTargetAudience = false,
  }) => {
    const [formState, dispatch] = useReducer(formReducer, initialState)
    const [isWarningModalOpen, setIsWarningModalOpen] = useState(false)
    const [isAudienceBreakdownLoading, setIsAudienceBreakdownLoading] =
      useState(false)
    const [audienceData, setAudienceData] =
      useState<AudienceBreakdownData | null>(null)

    // Use entity data hook
    const { orgs, teams, scheds, isLoadingEntities } = useEntityData({
      entity,
      isOpen,
      postToEdit,
    })

    /**
     * Helper function to check if HTML content is empty
     * Strips HTML tags and checks if remaining text is empty
     */
    const isContentEmpty = useCallback((content: string) => {
      if (!content) {
        return true
      }
      const text = content.replace(/<[^>]*>/g, '').trim()
      return text.length === 0
    }, [])

    const isSelectedTypeOrg = useMemo(() => {
      console.log('🔄 isSelectedTypeOrg', {
        entity,
        entityType,
        formState,
      })
      // Check if it's an org either from formState or from entityType when entity is pre-selected
      if (
        entity &&
        (entityType === 'organization' ||
          entityType === 'org' ||
          entityType === 'organizations')
      ) {
        return true
      }
      return formState.selectedType === 'org'
    }, [entity, entityType, formState])

    /**
     * Get the currently selected entity with its permissions
     * Similar to getSelectedEntity from TargetAudienceSelector
     */
    const getSelectedEntity = useCallback(() => {
      if (entity) {
        // If we have a direct entity prop, return it (it should have permissions)
        return entity
      }

      if (!formState.selected) {
        return null
      }

      // Search through all entities to find the selected one
      const all = [...orgs, ...teams, ...scheds]
      return all.find((e) => e.id === formState.selected)
    }, [entity, formState.selected, orgs, teams, scheds])

    /**
     * Check if the current user has update or admin permissions for the selected entity
     */
    const hasUpdateOrAdminPermission = useMemo(() => {
      const selectedEntity = getSelectedEntity()
      if (!selectedEntity) {
        return false
      }

      // If entity prop is provided, use the canTargetAudience prop (entity feeds)
      if (entity) {
        return canTargetAudience
      }

      // Check permissions from the permission object attached by filterForUpdatePermission
      const permission = selectedEntity.permission
      return permission?.update || permission?.admin
    }, [getSelectedEntity, entity, canTargetAudience])

    const { message } = App.useApp()
    const isHomePost = entityId === undefined

    const { createPost, updatePost } = useSocialFeed({
      id: entityId || formState.parentId || undefined,
      type: entityType as 'team' | 'schedule' | 'organization' | undefined,
      shouldFetch: false,
    })

    const handleToggleSection = (section: 'image' | 'link' | 'sendVia') => {
      dispatch({ type: 'TOGGLE_SECTION', section })
    }

    const handleSetSelected = useCallback(
      (value: any) => {
        dispatch({
          type: 'SET_FIELD',
          field: 'selected',
          payload: value,
        })
        // Clear audience data when entity changes
        if (value !== formState.selected) {
          setAudienceData(null)
        }
      },
      [formState.selected]
    )

    const handleSetSelectedType = useCallback((value: string | null) => {
      dispatch({
        type: 'SET_FIELD',
        field: 'selectedType',
        payload: value,
      })
    }, [])

    const handleSetTargetAudience = useCallback((value: number | null) => {
      dispatch({
        type: 'SET_FIELD',
        field: 'targetAudience',
        payload: value,
      })
    }, [])

    const handleSetParentId = useCallback((value: string) => {
      dispatch({
        type: 'SET_FIELD',
        field: 'parentId',
        payload: value,
      })
    }, [])

    const handleSetParentEntityType = useCallback(
      (value: 'team' | 'schedule' | 'organization' | undefined) => {
        dispatch({
          type: 'SET_FIELD',
          field: 'parentEntityType',
          payload: value,
        })
      },
      []
    )

    const handleAudienceBreakdownLoadingChange = useCallback(
      (isLoading: boolean) => {
        setIsAudienceBreakdownLoading(isLoading)
      },
      []
    )

    const handleAudienceDataChange = useCallback(
      (data: AudienceBreakdownData | null) => {
        setAudienceData(data)
      },
      []
    )

    // Use audience calculator hook (must be after callback definitions)
    const { calculateAudience } = useAudienceCalculator({
      selectedEntityId: entity ? entityId : formState.selected,
      selectedEntityType: entity
        ? entityType === 'organization'
          ? 'org'
          : entityType === 'schedule'
          ? 'sched'
          : entityType
        : formState.selectedType,
      activeCompetition: formState.activeCompetition,
      onLoadingChange: handleAudienceBreakdownLoadingChange,
      onDataChange: (data) => {
        handleAudienceDataChange(data)
        // Mark audience as calculated when data is received
        if (data) {
          dispatch({
            type: 'SET_FIELD',
            field: 'hasCalculatedAudience',
            payload: true,
          })
        }
      },
    })

    useEffect(() => {
      const orgEmpty = orgs.length === 0
      const teamEmpty = teams.length === 0
      const schedEmpty = scheds.length === 0
      if (!orgEmpty && !teamEmpty && !schedEmpty) {
        return
      }
      if (orgEmpty && !teamEmpty && schedEmpty) {
        dispatch({ type: 'SET_FIELD', field: 'selected', payload: teams[0].id })
        dispatch({ type: 'SET_FIELD', field: 'selectedType', payload: 'team' })
        dispatch({ type: 'SET_FIELD', field: 'parentId', payload: teams[0].id })
        dispatch({
          type: 'SET_FIELD',
          field: 'parentEntityType',
          payload: 'team',
        })
      }
      if (orgEmpty && teamEmpty && !schedEmpty) {
        dispatch({
          type: 'SET_FIELD',
          field: 'selected',
          payload: scheds[0].id,
        })
        dispatch({ type: 'SET_FIELD', field: 'selectedType', payload: 'sched' })
        dispatch({
          type: 'SET_FIELD',
          field: 'parentId',
          payload: scheds[0].id,
        })
        dispatch({
          type: 'SET_FIELD',
          field: 'parentEntityType',
          payload: 'schedule',
        })
      }
      if (!orgEmpty && teamEmpty && schedEmpty) {
        dispatch({ type: 'SET_FIELD', field: 'selected', payload: orgs[0].id })
        dispatch({ type: 'SET_FIELD', field: 'selectedType', payload: 'org' })
        dispatch({ type: 'SET_FIELD', field: 'parentId', payload: orgs[0].id })
        dispatch({
          type: 'SET_FIELD',
          field: 'parentEntityType',
          payload: 'organization',
        })
      }
    }, [orgs, teams, scheds, isOpen])

    useEffect(() => {
      dispatch({ type: 'SET_POST_TO_EDIT', payload: postToEdit })
    }, [postToEdit])

    // Initialize selectedType when entity is pre-selected
    useEffect(() => {
      if (entity && entityType) {
        // Map entityType to the format used in selectedType
        let mappedType = entityType
        if (entityType === 'organization') {
          mappedType = 'org'
        } else if (entityType === 'schedule') {
          mappedType = 'sched'
        }

        dispatch({
          type: 'SET_FIELD',
          field: 'selectedType',
          payload: mappedType,
        })

        // Also set the selected field and parent fields
        if (entityId) {
          dispatch({
            type: 'SET_FIELD',
            field: 'selected',
            payload: entityId,
          })
          dispatch({
            type: 'SET_FIELD',
            field: 'parentId',
            payload: entityId,
          })
          dispatch({
            type: 'SET_FIELD',
            field: 'parentEntityType',
            payload: entityType as 'team' | 'schedule' | 'organization',
          })
        }
      }
    }, [entity, entityType, entityId])

    // Auto-enable activeCompetition when org is selected
    useEffect(() => {
      if (
        formState.selectedType === 'org' ||
        (entity && entityType === 'organization')
      ) {
        dispatch({
          type: 'SET_FIELD',
          field: 'activeCompetition',
          payload: true,
        })
      }
    }, [formState.selectedType, entity, entityType])

    const performSubmit = useCallback(async () => {
      try {
        dispatch({ type: 'SET_FIELD', field: 'isLoading', payload: true })
        const { postToFeed, sendViaEmail, sendViaPush } = formState

        let entityTypeToSubmit = formState.parentEntityType || entityType
        if (entityTypeToSubmit === 'sched') {
          entityTypeToSubmit = 'schedule'
        }
        if (entityTypeToSubmit === 'org') {
          entityTypeToSubmit = 'organization'
        }
        if (postToEdit) {
          await updatePost(
            {
              send_social_feed: postToFeed ? true : false,
              send_email: sendViaEmail ? true : false,
              send_push_notification: sendViaPush ? true : false,
              can_reply: formState.canReply,
              active_competition: formState.activeCompetition,
              title: formState.title,
              body: sanitizeContentForSave(formState.content),
              body_plain_text: formState.body_plain_text,
              image: formState.image,
              // role_types: formState.visibleToRoles.map((role) =>
              //   parseInt(role, 10)
              // ),
              // All roles
              role_types: ROLE_TYPES.map((role) => role.id),
              url:
                formState.url &&
                !formState.url?.startsWith('http') &&
                formState.url?.length > 0 &&
                formState.url?.includes('.')
                  ? `https://${formState.url}`
                  : formState.url,
            } as any,
            postToEdit.id,
            formState.imageChanged
          )
        } else {
          await createPost(
            {
              send_social_feed: postToFeed ? true : false,
              send_email: sendViaEmail ? true : false,
              send_push_notification: sendViaPush ? true : false,
              can_reply: formState.canReply,
              active_competition: formState.activeCompetition,
              title: formState.title,
              body: sanitizeContentForSave(formState.content),
              body_plain_text: formState.body_plain_text,
              image: formState.image,
              // role_types: formState.visibleToRoles.map((role) =>
              //   parseInt(role, 10)
              // ),
              // All roles
              role_types: ROLE_TYPES.map((role) => role.id),
              url:
                formState.url &&
                !formState.url?.startsWith('http') &&
                formState.url?.length > 0 &&
                formState.url?.includes('.')
                  ? `https://${formState.url}`
                  : formState.url,
            } as any,
            entityTypeToSubmit as 'team' | 'schedule' | 'organization'
          )
        }
        message.open({
          type: 'success',
          content: postToEdit
            ? 'Communication Updated Successfully'
            : 'Communication Created Successfully',
        })
        dispatch({ type: 'RESET_FORM' })
        refreshNewFeed(1)
        setPostToEdit(null)
        setAudienceData(null) // Clear audience data after successful submission
        onClose()
      } catch (e: any) {
        if (e?.invalid_fields) {
          const newFieldErrors = [...formState.fieldErrors]
          const newCustomError = { ...formState.customError }

          if (e?.invalid_fields?.url) {
            newFieldErrors.push('website')
            newCustomError.website = e?.invalid_fields?.url
          }

          if (e?.invalid_fields?.role_types) {
            newFieldErrors.push('visibleToRoles')
            newCustomError.visibleToRoles = e?.invalid_fields?.role_types
          }

          if (newFieldErrors.length > formState.fieldErrors.length) {
            dispatch({
              type: 'SET_VALIDATION_ERRORS',
              fieldErrors: newFieldErrors,
              customError: newCustomError,
            })
            return
          }
        }
        const errorMessage = getErrorMessage(e)
        dispatch({ type: 'SET_FIELD', field: 'error', payload: errorMessage })
      } finally {
        dispatch({ type: 'SET_FIELD', field: 'isLoading', payload: false })
      }
    }, [
      formState,
      entityType,
      postToEdit,
      message,
      refreshNewFeed,
      setPostToEdit,
      onClose,
      updatePost,
      createPost,
    ])

    const handleSubmit = useCallback(async () => {
      dispatch({ type: 'SET_FIELD', field: 'error', payload: null })

      // Validation: check required fields before submit
      const newFieldErrors: string[] = []
      const newCustomError: { [key: string]: string } = {
        ...formState.customError,
      }

      if (formState.title.length === 0) {
        newFieldErrors.push('title')
        newCustomError.title = 'Title is required'
      } else {
        newCustomError.title = ''
      }

      if (isContentEmpty(formState.content)) {
        newFieldErrors.push('content')
        newCustomError.content = 'Content is required'
      } else {
        newCustomError.content = ''
      }

      dispatch({
        type: 'SET_VALIDATION_ERRORS',
        fieldErrors: newFieldErrors,
        customError: newCustomError,
      })

      if (newFieldErrors.length > 0) {
        return
      }

      // Check if notifications are enabled and show warning modal
      if (formState.sendViaEmail || formState.sendViaPush) {
        await calculateAudience()
        setIsWarningModalOpen(true)
        return
      }

      // Proceed with submission if no notifications
      await performSubmit()
    }, [
      calculateAudience,
      formState.content,
      formState.customError,
      formState.sendViaEmail,
      formState.sendViaPush,
      formState.title.length,
      isContentEmpty,
      performSubmit,
    ])

    const handleWarningModalClose = useCallback(() => {
      setIsWarningModalOpen(false)
    }, [])

    const handleWarningModalConfirm = useCallback(async () => {
      setIsWarningModalOpen(false)
      await performSubmit()
    }, [performSubmit])

    const getHeader = useCallback(() => {
      return (
        <div
          css={css`
            display: flex;
            flex-direction: row;
            align-items: center;
            gap: 16px;
          `}
        >
          <Text
            variant='heading3'
            weight='semiBold'
            color={colors.WHITE}
            textTransform='capitalize'
          >
            {postToEdit ? 'Edit Communication' : 'New Communication'}
          </Text>
          <Tag
            css={css`
              margin-top: 4px;
            `}
            color={colors.PRIMARY}
          >
            BETA
          </Tag>
        </div>
      )
    }, [postToEdit])

    const closeIcon = useCallback(() => {
      return (
        <CloseOutlined
          css={css`
            color: ${colors.SOLITUDE};
            font-size: 16px;
          `}
        />
      )
    }, [])

    const handleClose = useCallback(() => {
      dispatch({ type: 'RESET_FORM' })
      setPostToEdit(null)
      setAudienceData(null) // Clear audience data when closing modal
      onClose()
    }, [onClose, setPostToEdit])

    const getFooter = useCallback(() => {
      const noChannelSelected =
        !formState.postToFeed &&
        !formState.sendViaEmail &&
        !formState.sendViaPush
      const shouldDisableSave = isHomePost
        ? !formState.parentEntityType || !formState.parentId
        : !entityType || !entityId

      return (
        <div
          css={css`
            display: flex;
            justify-content: flex-end;
            gap: 16px;
            width: 50%;
            margin-left: auto;
          `}
        >
          <Button
            text='Cancel'
            onClick={handleClose}
            variant='secondary'
            disabled={formState.isLoading || isLoadingEntities}
          />
          <Button
            text='Save'
            onClick={handleSubmit}
            disabled={
              formState.isLoading ||
              isLoadingEntities ||
              isAudienceBreakdownLoading ||
              formState.fieldErrors.length > 0 ||
              shouldDisableSave ||
              isContentEmpty(formState.content) ||
              formState.title.length === 0 ||
              noChannelSelected
            }
            isLoading={formState.isLoading}
            variant='primary'
          />
        </div>
      )
    }, [
      formState.content,
      formState.fieldErrors.length,
      formState.isLoading,
      formState.parentEntityType,
      formState.parentId,
      formState.title.length,
      formState.postToFeed,
      formState.sendViaEmail,
      formState.sendViaPush,
      entityId,
      entityType,
      handleClose,
      handleSubmit,
      isHomePost,
      isAudienceBreakdownLoading,
      isLoadingEntities,
      isContentEmpty,
    ])

    return (
      <Modal
        css={styles}
        footer={getFooter()}
        closeIcon={closeIcon()}
        title={getHeader()}
        open={isOpen}
        classNames={classNames}
        styles={modalStyles}
        width={800}
        centered
        onCancel={handleClose}
      >
        <div
          css={css`
            display: flex;
            flex-direction: column;
            position: relative;
            padding-bottom: 16px;
            padding-top: 16px;
          `}
        >
          {/* Show loading spinner when loading entities in edit mode */}
          {isLoadingEntities && postToEdit && (
            <div
              css={css`
                position: absolute;
                top: 0;
                left: 0;
                right: 0;
                bottom: 0;
                background: rgba(39, 48, 62, 0.95);
                display: flex;
                justify-content: center;
                align-items: center;
                z-index: 10;
              `}
            >
              <Spin size='small' />
            </div>
          )}
          <PostHeader
            selfUser={selfUser}
            entity={entity}
            entityType={entityType}
            orgs={orgs}
            teams={teams}
            scheds={scheds}
            selected={formState.selected}
            setSelected={handleSetSelected}
            selectedType={formState.selectedType}
            setSelectedType={handleSetSelectedType}
            targetAudience={formState.targetAudience}
            setTargetAudience={handleSetTargetAudience}
            setParentId={handleSetParentId}
            setParentEntityType={handleSetParentEntityType}
            isLoading={formState.isLoading}
            postToEdit={postToEdit}
            isLoadingEntities={isLoadingEntities}
            showImage={formState.showImage}
            showLink={formState.showLink}
            showSendVia={formState.showSendVia}
            onToggleSection={handleToggleSection}
          />

          {formState.showSendVia &&
            hasUpdateOrAdminPermission &&
            !postToEdit && (
              <div
                css={css`
                  align-self: stretch;
                  flex-direction: column;
                  justify-content: flex-start;
                  align-items: flex-start;
                  gap: 8px;
                  display: flex;
                  margin-bottom: 16px;
                `}
              >
                {/* Side-by-side layout: Communication Channels (left) and Audience Breakdown (right) */}
                <div
                  css={css`
                    display: flex;
                    gap: 24px;
                    align-self: stretch;
                    width: 100%;
                  `}
                >
                  {/* Left side - Communication Channel Checkboxes */}
                  <div
                    css={css`
                      flex: 1;
                      min-width: 0;
                    `}
                  >
                    <CommunicationChannel
                      postToFeed={formState.postToFeed}
                      sendViaEmail={formState.sendViaEmail}
                      sendViaPush={formState.sendViaPush}
                      dispatch={dispatch}
                      isSelectedTypeOrg={isSelectedTypeOrg}
                      hasUpdateOrAdminPermission={hasUpdateOrAdminPermission}
                    />
                  </div>

                  {/* Vertical Divider */}
                  {hasUpdateOrAdminPermission && (
                    <div
                      css={css`
                        width: 1px;
                        background: ${colors.SECONDARY_100};
                        align-self: stretch;
                      `}
                    />
                  )}

                  {/* Right side - Audience Breakdown */}
                  {hasUpdateOrAdminPermission && (
                    <div
                      css={css`
                        flex: 1;
                        min-width: 0;
                        display: flex;
                        flex-direction: column;
                        justify-content: space-between;
                      `}
                    >
                      <AudienceBreakdownSection
                        selectedEntityId={
                          entity ? entityId : formState.selected
                        }
                        selectedEntityType={
                          entity
                            ? entityType === 'organization' ||
                              entityType === 'org' ||
                              entityType === 'organizations'
                              ? 'org'
                              : entityType === 'schedule'
                              ? 'sched'
                              : entityType
                            : formState.selectedType
                        }
                        audienceData={audienceData}
                        isLoading={isAudienceBreakdownLoading}
                        onCalculate={calculateAudience}
                        isCalculating={isAudienceBreakdownLoading}
                        hasCalculated={formState.hasCalculatedAudience}
                      />
                    </div>
                  )}
                </div>

                {/* Horizontal Divider */}
                <div
                  css={css`
                    width: 100%;
                    height: 1px;
                    background: ${colors.SECONDARY_100};
                  `}
                />
              </div>
            )}

          <PostSettings
            canReply={formState.canReply}
            activeCompetition={formState.activeCompetition}
            dispatch={dispatch}
            isSelectedTypeOrg={isSelectedTypeOrg}
            hasUpdateOrAdminPermission={hasUpdateOrAdminPermission}
            postToFeed={formState.postToFeed}
            visibleToRoles={formState.visibleToRoles}
            fieldErrors={formState.fieldErrors}
            customError={formState.customError}
          />

          <PostForm
            title={formState.title}
            content={formState.content}
            url={formState.url}
            image={formState.image}
            isLoading={formState.isLoading}
            showImage={formState.showImage}
            showLink={formState.showLink}
            fieldErrors={formState.fieldErrors}
            customError={formState.customError}
            dispatch={dispatch}
          />

          {formState.error && (
            <Alert
              message={formState.error}
              type='error'
              css={css`
                margin-top: 16px;
              `}
            />
          )}
        </div>
        <NotificationWarningModal
          isOpen={isWarningModalOpen}
          onClose={handleWarningModalClose}
          onConfirm={handleWarningModalConfirm}
          sendViaEmail={formState.sendViaEmail}
          sendViaPush={formState.sendViaPush}
          isLoading={formState.isLoading}
          audienceData={audienceData}
          isLoadingAudience={isAudienceBreakdownLoading}
        />
      </Modal>
    )
  }
)

const makeMapStateToProps = () => {
  return (state) => {
    return {
      selfUser: getSelfUser(state),
    }
  }
}
const mapDispatchToProps = (_dispatch) => ({})

export default connect(
  makeMapStateToProps,
  mapDispatchToProps
)(FeedPostCreateModal)
